import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Row, Group, Label } from '@common/components/form';
import { TextareaInputHookForm as TextareaInput } from '@common/components/form/inputs/textarea/textarea';
import { SelectInputHookForm as SelectInput } from '@common/components/form/inputs/select';
import { DialogWrapper } from '@common/components/modal/dialog-wrapper';
import { OverviewSection } from '@common/components/overview';
import Card from '@common/components/card';
import ProfileImage from '@common/components/profile-image';
import Icon from '@common/components/icon';
import { useAppSelector, useThunkDispatch } from '@common/hooks';
import { useRequest } from '@common/services/api';
import { CompanyValue } from '@modules/admin/types';
import * as organisationSelectors from '@modules/organisation/selectors/organisation';
import { sendHighFive } from '../../actions/send-high-five';
import { AlertService } from '@common/services/alert';
import { useTranslation } from 'react-i18next';
import { ExtendedUser, LoggedUser } from '@common/types/objects';

const baseClasses = [
  'tw-px-[12px]',
  'tw-py-[8px]',
  'tw-rounded-full',
  'tw-border',
  'tw-border-solid',
  'tw-border-neutral-300',
  'tw-cursor-pointer',
  'tw-select-none',
].join(' ');

const selectedClasses = [
  'tw-bg-[hsl(var(--color-primary-hue),var(--color-primary-saturation),95%)]',
  'tw-text-[var(--color-primary)]',
  '!tw-border-[var(--color-primary-dull)]',
  'tw-font-medium',
].join(' ');

interface HighfiveFormProps {
  show: boolean;
  onClose: () => void;
  recipientUserId: ExtendedUser | LoggedUser;
  onSubmit: (values: any) => void;
}

interface FormValues {
  text: string;
  selectedValues: string[];
  network: string;
  recipientUserId: string;
}

interface CompanyValuesResponse {
  data: CompanyValue[];
  meta: null;
}

const HighfiveForm: React.FC<HighfiveFormProps> = ({
  show,
  onClose,
  recipientUserId,
}) => {
  const thunkDispatch = useThunkDispatch();
  const { t } = useTranslation();
  const organisation = useAppSelector(organisationSelectors.selected);
  const networks = useAppSelector(organisationSelectors.networks);

  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [valuesError, setValuesError] = useState<string>('');

  const { control, formState, handleSubmit, reset } = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      text: '',
      selectedValues: [],
      network: '',
      recipientUserId: recipientUserId.id,
    },
  });

  const { data } = useRequest<CompanyValuesResponse>(
    `/v1/organisations/${organisation.id}/values`
  );

  const options = useMemo(() => {
    return networks.filter(({ id }) => recipientUserId?.scopes?.networks?.some(
        (userNetwork) => userNetwork.id === id
      )
    );
  }, [networks, recipientUserId?.scopes?.networks]);

  const handleValueSelection = (valueId: string) => {
    setSelectedValues((prev) => {
      if (prev.includes(valueId)) {
        setValuesError('');
        return prev.filter((id) => id !== valueId);
      }
      if (prev.length >= 3) {
        setValuesError(t('common:highfive_max_values_error'));
        return prev;
      }
      setValuesError('');
      return [...prev, valueId];
    });
  };

  const onSubmit = async (values: FormValues) => {
    try {
      setSubmitting(true);
      const payload = {
        recipient_user_id: recipientUserId.id,
        text: values.text,
        values: selectedValues,
        ...(values.network && {
          timeline: {
            id: values.network,
            type: 'network' as const,
          },
        }),
      };

      await thunkDispatch(sendHighFive(payload));
      AlertService.success(t('common:highfive_success'));
      reset();
      setSelectedValues([]);
      onClose();
    } catch (error) {
      console.error('Failed to send high five:', error);
      AlertService.error(t('common:highfive_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogWrapper
      show={show}
      onHide={onClose}
      size="small"
      className="HighfiveModalForm"
      confirmButtonText={t('common:highfive_send_button')}
      confirmButtonType="submit"
      confirmButtonDisabled={submitting || !formState.isValid}
      hideHeader
      confirmButtonLoading={submitting}
      onConfirm={handleSubmit(onSubmit)}
      content={
        <div className="tw-flex tw-flex-col tw-gap-6">
          <Card
            containerClassName="relative !tw-bg-[var(--color-primary)]
          tw-bg-[url('/static/images/recognition_bg.png')]
          tw-bg-size-50% tw-bg-no-repeat tw-bg-[position:top_right]"
          >
            <Card.Content className="tw-flex tw-flex-col tw-gap-4 tw-p-0">
              <div className="tw-flex tw-items-center tw-justify-center">
                <div className="tw-flex tw-z-10 tw-object-cover tw-border-[1px] tw-border-solid tw-border-white tw-rounded-full">
                  <ProfileImage size={50} user={recipientUserId} />
                </div>
                <div
                  className="tw-flex tw-z-20 !tw-bg-[var(--color-primary)]
                tw-items-center tw-justify-center tw-rounded-full tw-ml-[-10px] tw-w-[50px] tw-h-[50px]
                tw-object-cover tw-border-[1px] tw-border-solid tw-border-white"
                >
                  <Icon
                    size={40}
                    className="tw-text-white"
                    type="highfive__filled"
                  />
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-justify-center tw-flex-col">
                <div className="tw-text-2xl tw-m-0 tw-font-inter tw-font-medium tw-text-white tw-leading-snug">
                  {t('common:send_highfive')}
                </div>
                <div className="tw-text-base tw-m-0 tw-font-inter tw-font-medium tw-text-white tw-leading-snug">
                  to {recipientUserId.full_name}
                </div>
              </div>
            </Card.Content>
          </Card>
          <Row style={{ marginBottom: 0 }}>
            <Group>
              <Label text={t('common:highfive_message')} />
              <TextareaInput
                className="!tw-h-[100px]"
                name="text"
                control={control}
                placeholder={t('common:highfive_write_message')}
                minRows={3}
                rules={{ required: 'Please write a message' }}
              />
            </Group>
          </Row>
          {data?.length > 0 && (
            <Row>
              <Group>
                <Label text={t('common:highfive_select_values')} />
                <OverviewSection className="!tw-border !tw-border-solid !tw-mt-0 !tw-border-gray-200 !tw-rounded-lg !tw-p-4">
                  <Row>
                    <Group>
                      <div className="tw-flex tw-flex-wrap tw-gap-2">
                        {data.map((option: CompanyValue) => {
                          const isSelected = selectedValues.includes(option.id);

                          const classNames = `${baseClasses} ${
                            isSelected ? selectedClasses : ''
                          }`;
                          return (
                            <div
                              key={option.id}
                              className={classNames}
                              onClick={() => handleValueSelection(option.id)}
                            >
                              {option.name}
                            </div>
                          );
                        })}
                      </div>
                    </Group>
                  </Row>
                </OverviewSection>
                {valuesError && (
                  <div className="tw-text-red-500 tw-mt-2 tw-text-sm">
                    {valuesError}
                  </div>
                )}
              </Group>
            </Row>
          )}
          <Row>
            <Group>
              <Label text={t('common:highfive_share_with_specific_group')} />
              <SelectInput
                searchable
                name="network"
                control={control}
                options={options}
                labelKey="name"
                valueKey="id"
                placeholder={t('common:highfive_select_network')}
              />
            </Group>
          </Row>
        </div>
      }
    />
  );
};

export default HighfiveForm;
